import * as React from 'react';
import { Tooltip } from 'antd';
import { RequiredFieldIcon } from '../../icons';

interface CustomInputLabelProps {
    label: string;
    labelTooltip?: string;
    required?: boolean;
}

const CustomInputLabel: React.FC<CustomInputLabelProps> = ({ label, labelTooltip, required }) => {
    // Prevent label from animating when the component is first rendered
    const [transitionEnabled, setTransitionEnabled] = React.useState(false);
    const labelRef = React.useRef<HTMLLabelElement>(null);

    // Tooltip needs pointer events. When it fires we block the label from taking focus
    // and instead focus the input sitting next to it.
    const handleMouseDown = React.useCallback(
        (event: React.MouseEvent<HTMLLabelElement>) => {
            if (!labelTooltip) {
                return;
            }

            const currentLabel = labelRef.current;

            if (!currentLabel) {
                return;
            }

            event.preventDefault();

            const container = currentLabel.parentElement;

            if (!container) {
                return;
            }

            const focusTarget = container.querySelector<HTMLElement>(
                'input:not([type="hidden"]), textarea, select, [contenteditable="true"]'
            );

            focusTarget?.focus();
        },
        [labelTooltip]
    );

    // No tooltip: keep the label inert by cancelling the click
    const handleClickCapture = React.useCallback(
        (event: React.MouseEvent<HTMLLabelElement>) => {
            if (!labelTooltip) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [labelTooltip]
    );

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setTransitionEnabled(true);
        }, 100);

        return () => clearTimeout(timeout);
    }, []);

    return (
        <Tooltip title={labelTooltip}>
            <label
                ref={labelRef}
                className="custom-input-label"
                onMouseDown={handleMouseDown}
                onClickCapture={handleClickCapture}
                style={{
                    transition: transitionEnabled ? '0.1s linear' : 'none',
                    // Tooltip on => enable pointer events; otherwise leave the label inert.
                    pointerEvents: labelTooltip ? 'auto' : 'none'
                }}
            >
                {label} {!!required && <RequiredFieldIcon />}
            </label>
        </Tooltip>
    );
};

export default CustomInputLabel;
